import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Plus, X, PlusCircle, Layers, BookOpen, Camera } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/api";

const ACTIONS = [
  { id: "holding", label: "Add Holding", icon: PlusCircle, color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  { id: "asset", label: "Add Asset", icon: Layers, color: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  { id: "journal", label: "Journal Entry", icon: BookOpen, color: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  { id: "snapshot", label: "Snapshot Now", icon: Camera, color: "bg-violet-500/15 text-violet-400 border-violet-500/30" },
];

// Pages where FAB adds value
const FAB_PAGES = ["/dashboard", "/holdings", "/networth", "/income", "/actions", "/explorer", "/analytics", "/risk", "/review"];

export default function QuickAddFAB() {
  const [open, setOpen] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  const showFAB = FAB_PAGES.includes(location.pathname);

  // Close menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [open]);

  if (!showFAB) return null;

  const handleAction = async (id) => {
    setOpen(false);
    switch (id) {
      case "holding":
        navigate("/manual-entry");
        break;
      case "asset":
        navigate("/networth", { state: { openAssetSheet: true } });
        break;
      case "journal":
        navigate("/journal", { state: { openForm: true } });
        break;
      case "snapshot":
        setSnapping(true);
        try {
          const [portfolioRes, nwRes] = await Promise.all([
            authFetch("/api/timeline/snapshot", { method: "POST" }),
            authFetch("/api/networth/timeline/snapshot", { method: "POST" }),
          ]);
          const pData = await portfolioRes.json();
          const nData = await nwRes.json();
          if (portfolioRes.ok || nwRes.ok) {
            toast.success("Snapshot captured", { description: pData.message || nData.message || "Portfolio & Net Worth snapshot saved" });
          } else {
            toast.info(pData.message || nData.message || "No data to snapshot");
          }
        } catch {
          toast.error("Snapshot failed");
        }
        setSnapping(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={menuRef} className="fixed z-[55] md:hidden" style={{ bottom: "calc(4.5rem + env(safe-area-inset-bottom, 0px))", right: "1rem" }}>
      {/* Action menu */}
      <div className={`absolute bottom-14 right-0 flex flex-col gap-2 items-end transition-all duration-200 ${open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-3 pointer-events-none"}`}>
        {ACTIONS.map((action, i) => {
          const Icon = action.icon;
          return (
            <button
              key={action.id}
              data-testid={`fab-action-${action.id}`}
              onClick={() => handleAction(action.id)}
              disabled={action.id === "snapshot" && snapping}
              className={`flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full border backdrop-blur-md shadow-lg transition-all duration-200 ${action.color} hover:scale-[1.03] active:scale-95`}
              style={{ transitionDelay: open ? `${i * 40}ms` : "0ms" }}
            >
              <Icon className="h-4 w-4" />
              <span className="text-xs font-medium whitespace-nowrap">{action.label}</span>
            </button>
          );
        })}
      </div>

      {/* FAB button */}
      <button
        data-testid="quick-add-fab"
        onClick={() => setOpen(!open)}
        className={`w-12 h-12 rounded-full shadow-xl flex items-center justify-center transition-all duration-200 active:scale-90 ${
          open
            ? "bg-muted/90 border border-border text-foreground rotate-45"
            : "bg-primary text-primary-foreground hover:bg-primary/90"
        }`}
      >
        {open ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
      </button>

      {/* Backdrop scrim */}
      {open && <div className="fixed inset-0 -z-10 bg-black/20 backdrop-blur-[2px]" onClick={() => setOpen(false)} />}
    </div>
  );
}
